/** A read-only line for an RTP-moving lever that lives on another tab. */
export interface RtpContributor {
  /** Display label, e.g. "Jackpot contribution". */
  label: string;
  /** Tab id to deep-link to. */
  tab: 'jackpot' | 'category-bonus' | 'streak';
  /** Short note on how it moves RTP. */
  note: string;
}

/**
 * Per-game shape of the unified RTP tab. Pure data — no DOM. `hasSolver` marks
 * a one-number Target-RTP solver. sicbo/dragontiger use the closed-form affine
 * bps solver; greedyking uses a numeric (simulate-based) solver that scales all
 * food multipliers to a target OVERALL RTP — both flow through the same panel +
 * endpoint. `solverKind` lets the panel tailor its copy.
 */
export interface RtpSurfaceDescriptor {
  gameId: string;
  hasWeights: boolean;
  hasMultipliers: boolean;
  hasSolver: boolean;
  /** How the solver targets RTP: per-bet (affine) or overall (numeric scale).
   *  null when there is no solver. Drives the panel's copy. */
  solverKind: 'per-bet' | 'overall' | null;
  contributors: RtpContributor[];
}

const DEFAULT_DESCRIPTOR: Omit<RtpSurfaceDescriptor, 'gameId'> = {
  hasWeights: true,
  hasMultipliers: true,
  hasSolver: false,
  solverKind: null,
  contributors: [],
};

const BY_GAME: Record<string, Omit<RtpSurfaceDescriptor, 'gameId'>> = {
  // Crash has no weight table: the outcome is a single multiplier derived from
  // the seed, not a pick from a weighted set. Its `multipliers` surface carries
  // exactly one field, `rtpBps` — which IS the RTP, because `P(reach x) = k/x`
  // and a win pays `x`, so the expected return is `k` at every target.
  //
  // `hasSolver: false` is deliberate and is NOT the #427 mistake. That was a
  // game whose RTP could be reported but not inverted. Here there is nothing to
  // invert: the field an admin edits already IS what a solver would return, so
  // a solver would be a button that computes `k = target`. Crash is in
  // RTP_REPORTABLE_GAMES so the headline renders a real figure.
  crash: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: false,
    solverKind: null,
    contributors: [],
  },
  sicbo: {
    hasWeights: true,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [
      { label: 'Jackpot contribution', tab: 'jackpot', note: 'A jackpot rake lowers base RTP.' },
    ],
  },
  dragontiger: {
    hasWeights: true,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  greedyking: {
    hasWeights: true,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'overall',
    contributors: [
      { label: 'Jackpot contribution', tab: 'jackpot', note: 'Pool rake lowers base RTP.' },
      { label: 'Category bonus', tab: 'category-bonus', note: 'Extra payouts raise RTP.' },
      { label: 'Streak bonus', tab: 'streak', note: 'Streak rewards raise RTP.' },
    ],
  },
  // Flat bps paytable, no RNG weight surface (digits are a fixed uniform 1/10 per
  // position) and no bps target-RTP solver (it is absent from admin-api's
  // RTP_SOLVABLE_GAMES). The tunable surface is the payout multipliers: the 1.90
  // Big/Small/Odd/Even cells and the 9.50 exact-first-digit (0–9) cells.
  singaporelottery: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: false,
    solverKind: null,
    contributors: [],
  },
  // Fixed uniform card draw + fixed drawing rules, so there is no RNG weight
  // surface. Tunable RTP comes only from the payout multipliers (tie 8:1 vs
  // 9:1, pair odds, and the banker commission baked into its 1.95 return).
  // Each cell's RTP is affine in its own multiplier, so the per-bet bps solver
  // inverts cleanly — no weights to interact with and no crit cap to clip it.
  // OPERATOR NOTE: solving rewrites the standard 1:1 / 0.95:1 / 8:1 / 11:1
  // odds into non-standard numbers, which players recognise. Prefer editing one
  // multiplier and reading the exact recomputed RTP; use the solver only when a
  // specific house edge matters more than a conventional-looking table.
  // Both Dice Rush tables: three fair dice, uniform and NOT admin-tunable, so
  // there is no RNG weight surface at all. RTP is tuned purely through the
  // sixteen payout multipliers, and because each area's win probability is a
  // constant, its RTP is affine in its own multiplier — the per-bet bps solver
  // inverts it exactly, with no weights to interact with.
  //
  // OPERATOR NOTE: solving rewrites the round 1.92 / 2.28 / 12.96 figures into
  // numbers players do not recognise, AND a rate whose gcd with 10000 drops
  // below 400 takes the chip ladder off the 25-credit stake grid, where every
  // win is floored down. Prefer editing one multiplier and reading the exact
  // recomputed RTP; use the solver only when a specific house edge matters more.
  shortdicerush: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  longdicerush: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  baccarat: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
};

export function descriptorFor(gameId: string): RtpSurfaceDescriptor {
  const base = BY_GAME[gameId] ?? DEFAULT_DESCRIPTOR;
  return { gameId, ...base };
}

/** Map retired tab ids (`rng`, `multipliers`) onto the unified `rtp` tab. */
export function aliasRtpTab<T extends string | null>(tab: T): T | 'rtp' {
  if (tab === 'rng' || tab === 'multipliers') return 'rtp';
  return tab;
}
